import DOMPurify from 'dompurify';

/**
 * Sanitize HTML/SVG for safe use with dangerouslySetInnerHTML.
 *
 * The editor renders raw SVG markup from uploaded files and design assets
 * via dangerouslySetInnerHTML.  DOMPurify's default HTML profile strips most
 * SVG vocabulary, so we enable the full SVG + SVG-Filters profiles and add
 * the remaining primitive tags/attributes Illustrator/Figma exports rely on
 * (feColorMatrix for metallic & duotone, lighting primitives, turbulence,
 * displacement, etc.).
 *
 * Security: scripts, iframes, foreignObject, and event-handler attributes
 * remain forbidden — those are XSS surfaces.
 */
const SVG_PROFILE = {
    USE_PROFILES: { svg: true, svgFilters: true, html: true },

    ADD_TAGS: [
      // Filter primitives missing from / inconsistently covered by the SVG-Filters profile
      'feColorMatrix', 'feComponentTransfer',
      'feFuncR', 'feFuncG', 'feFuncB', 'feFuncA',
      'feMorphology', 'feTurbulence', 'feDisplacementMap', 'feConvolveMatrix',
      'feSpecularLighting', 'feDiffuseLighting',
      'feDistantLight', 'fePointLight', 'feSpotLight',
      'feImage', 'feTile',
      // Misc SVG vocabulary the editor relies on
      'marker', 'style', 'title', 'desc',
    ],

    ADD_ATTR: [
      // feColorMatrix / feComponentTransfer
      'values', 'type', 'tableValues',
      'slope', 'intercept', 'amplitude', 'exponent',
      // feConvolveMatrix
      'kernelMatrix', 'divisor', 'bias', 'targetX', 'targetY',
      'edgeMode', 'preserveAlpha',
      // feTurbulence
      'baseFrequency', 'numOctaves', 'seed', 'stitchTiles',
      // feDisplacementMap
      'xChannelSelector', 'yChannelSelector', 'scale',
      // feMorphology
      'radius', 'operator',
      // Lighting (fe(Specular|Diffuse)Lighting + light sources)
      'azimuth', 'elevation', 'specularExponent', 'surfaceScale',
      'specularConstant', 'diffuseConstant', 'kernelUnitLength',
      'lighting-color', 'pointsAtX', 'pointsAtY', 'pointsAtZ', 'limitingConeAngle',
      // Mask / pattern / filter geometry
      'maskUnits', 'maskContentUnits',
      'patternUnits', 'patternContentUnits', 'patternTransform',
      'primitiveUnits',
      // CSS interop
      'mix-blend-mode', 'vector-effect',
    ],

    FORBID_TAGS: ['script', 'iframe', 'foreignObject'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
};

export function sanitizeHTML(dirty) {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, SVG_PROFILE);
}

/** On <use>, keep only references inside the same SVG (#id). */
const keepInternalUseRefs = (node) => {
  if (!node.nodeName || node.nodeName.toLowerCase() !== 'use') return;
  ['href', 'xlink:href'].forEach((name) => {
    const v = node.getAttribute(name);
    if (v !== null && !v.trim().startsWith('#')) node.removeAttribute(name);
  });
};

/**
 * Sanitize SVG markup that is inserted into the page — every SVG element in the
 * editor, thumbnails, gallery, shared designs and admin review goes through
 * processSvgForRender → here. sanitizeHTML's profile, plus <use> limited to
 * same-SVG references, as Illustrator/Figma symbol exports need.
 */
export function sanitizeSvgMarkup(dirty) {
  if (!dirty) return '';
  DOMPurify.addHook('afterSanitizeAttributes', keepInternalUseRefs);
  try {
    return DOMPurify.sanitize(dirty, {
      ...SVG_PROFILE,
      ADD_TAGS: [...SVG_PROFILE.ADD_TAGS, 'use'],
      ADD_ATTR: [...SVG_PROFILE.ADD_ATTR, 'href', 'xlink:href'],
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}

/**
 * Sanitize blog / rich-text HTML — stricter than sanitizeHTML because no
 * SVG vocabulary is needed.
 */
export function sanitizeBlogHTML(dirty) {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty);
}

/**
 * URL schemes safe to hand the editor as an image source or page background.
 * Everything else — `javascript:`, `vbscript:`, `file:`, `data:text/html` —
 * is an XSS or exfiltration surface once it lands in an `src` or a CSS `url()`.
 */
const SAFE_URL_SCHEMES = ['https:', 'http:', 'blob:'];

/** `data:` URLs are safe only when they actually carry an image. */
const SAFE_DATA_URL = /^data:image\/(png|jpe?g|gif|webp|avif|svg\+xml);/i;

/** Control characters are used to smuggle a scheme past naive checks. */
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * Return `url` when it is safe to use as an asset source, else null.
 *
 * Relative paths are trusted: they resolve against our own origin and cannot
 * carry a scheme. Anything with a scheme must be on the allow-list. Never
 * returns a partially-cleaned URL — a URL is either safe as-is or rejected,
 * because "cleaning" a hostile URL is how bypasses get written.
 *
 * @param {*} url
 * @returns {(string|null)}
 */
export function sanitizeAssetUrl(url) {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed || CONTROL_CHARS.test(trimmed)) return null;

  if (SAFE_DATA_URL.test(trimmed)) return trimmed;

  // No scheme → a relative path on our own origin.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;

  try {
    const { protocol } = new URL(trimmed, 'https://ivisora.invalid');
    return SAFE_URL_SCHEMES.includes(protocol) ? trimmed : null;
  } catch {
    return null; // unparseable → untrusted
  }
}
